import React from 'react';
import { LegalBlock } from '../../services/api';

/**
 * Sunucudan gelen hukuki metni çizen tek bileşen.
 *
 * Metin sunucuda blok listesi (heading/paragraph/list/links/table) olarak
 * duruyor, HTML olarak değil — aynı gövdeyi mobil uygulama da render ediyor
 * ve orada HTML çalıştırmak WebView demek. Bu bileşen o beş bloğun web
 * karşılığıdır; mobildeki eşi `dijipo-mobile/components/LegalBody.tsx`.
 *
 * Sunucudan HTML gelmediği için `dangerouslySetInnerHTML` de yok — metin
 * ne olursa olsun metin olarak basılır.
 */
const LegalBody: React.FC<{ body: LegalBlock[] }> = ({ body }) => (
  <>
    {body.map((block, i) => {
      switch (block.type) {
        case 'heading':
          return (
            <h2
              key={i}
              className="mt-8 mb-3 text-[16px] font-bold leading-snug text-[#2b2b2b] first:mt-0 lg:text-[18px]"
            >
              {block.text}
            </h2>
          );

        case 'paragraph':
          return (
            <p key={i} className="mb-4 text-[14px] leading-[1.75] text-[#4a5260] lg:text-[15px]">
              {block.text}
            </p>
          );

        case 'list':
          return (
            <ul key={i} className="mb-4 list-disc space-y-2 pl-5 text-[14px] leading-[1.7] text-[#4a5260] lg:text-[15px]">
              {block.items.map((item, j) => (
                <li key={j}>{item}</li>
              ))}
            </ul>
          );

        case 'links':
          return (
            <ul key={i} className="mb-4 space-y-2 text-[14px] leading-[1.7] lg:text-[15px]">
              {block.items.map((item, j) => (
                <li key={j}>
                  <span className="text-[#4a5260]">{item.label}: </span>
                  {/* Dış bağlantı — noreferrer, referrer'ı üçüncü tarafa sızdırmamak için. */}
                  <a
                    href={item.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="break-all font-medium text-[#3973bf] hover:underline"
                  >
                    {item.url}
                  </a>
                </li>
              ))}
            </ul>
          );

        case 'table':
          return (
            // Envanter tablosu dar ekranda taşar; yatay kaydırma sayfanın
            // tamamına değil tablonun kendisine ait olmalı.
            <div key={i} className="mb-6 -mx-1 overflow-x-auto px-1">
              <table className="w-full min-w-[560px] border-collapse text-left text-[13px] lg:text-[14px]">
                <thead>
                  <tr>
                    {block.columns.map((col, j) => (
                      <th
                        key={j}
                        className="border-b-2 border-[#e6e9ef] bg-[#f7f9fc] px-3 py-2.5 font-semibold text-[#2b2b2b]"
                      >
                        {col}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {block.rows.map((row, j) => (
                    <tr key={j} className="align-top">
                      {row.map((cell, k) => (
                        <td
                          key={k}
                          className={`border-b border-[#eef1f6] px-3 py-2.5 leading-[1.6] text-[#4a5260] ${
                            k === 0 ? 'whitespace-nowrap font-medium text-[#2b2b2b]' : ''
                          }`}
                        >
                          {cell}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );

        default:
          return null;
      }
    })}
  </>
);

export default LegalBody;
